import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useDrippers } from "../hooks/useDrippers";
import { useGrinders } from "../hooks/useGrinders";
import { fmtRatio, brewParamChips, suggestRecipe, parseMinSec, fmtMinSec, suggestGrind, naturalFactor } from "../lib/brew";
import type { Coffee, Impression, BrewFeedEntry, BrewInput, BrewRecipe } from "../types";

// Empty brew form. Numeric fields are kept as strings while editing and coerced on submit.
// Defaults reflect the owner's standard iced filter recipe (15g dose, 180g water in →
// 145g out, +60g dilution). `dripper` / `grinder` hold the selected ids as strings; `frozen` is
// "1" (ground from frozen), "0" (fresh/thawed) or "" (unknown).
const EMPTY = {
  text: "", score: "", brew_type: "iced", dilution: "60", dose: "15", water_in: "180", water_out: "145", time: "", temp: "",
  grind: "", microns: "", tags: "", adjustment: "", nextTime: "", dripper: "", grinder: "", rpm: "", pour: "", frozen: "",
};
type FormState = typeof EMPTY;

// Turn the string form into a BrewInput, dropping blanks so unset fields stay null server-side.
function toBrewInput(f: FormState): BrewInput {
  const n = (s: string) => (s.trim() === "" ? undefined : Number(s));
  const s = (v: string) => (v.trim() === "" ? undefined : v.trim());
  return {
    text: f.text.trim() || undefined,
    score: n(f.score),
    brew_type: f.brew_type,
    dilution_g: n(f.dilution),
    dose_g: n(f.dose),
    water_in_g: n(f.water_in),
    water_out_g: n(f.water_out),
    time_s: parseMinSec(f.time),
    temp_c: n(f.temp),
    grind: s(f.grind),
    grind_microns: n(f.microns),
    flavor_tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
    adjustment: s(f.adjustment),
    next_time: s(f.nextTime),
    dripper_id: f.dripper.trim() === "" ? undefined : Number(f.dripper),
    grinder_id: f.grinder.trim() === "" ? undefined : Number(f.grinder),
    rpm: n(f.rpm),
    pour_structure: s(f.pour),
    frozen: f.frozen === "" ? undefined : f.frozen === "1",
  };
}

export function BrewLogSection({ coffee, impressions, setImpressions, brewFeed, onConsumed, onCoffeeChange }: {
  coffee: Coffee;
  impressions: Impression[];
  setImpressions: React.Dispatch<React.SetStateAction<Impression[]>>;
  brewFeed: BrewFeedEntry[];
  onConsumed: (coffee: Coffee | null) => void;
  onCoffeeChange: (coffee: Coffee) => void;
}) {
  const [f, setF] = useState<FormState>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const set = (k: keyof FormState, v: string) => setF((cur) => ({ ...cur, [k]: v }));
  const { data: drippers } = useDrippers();
  const activeDrippers = useMemo(() => (drippers || []).filter((d) => d.active), [drippers]);
  const { data: grinders } = useGrinders();
  // Active grinders, plus the selected one if it's retired (editing an old brew) so the select can show it.
  const grinderOptions = useMemo(
    () => (grinders || []).filter((g) => g.active || String(g.id) === f.grinder),
    [grinders, f.grinder]
  );

  const portions = coffee.portions;
  const ratioPreview = fmtRatio(Number(f.dose) || null, Number(f.water_in) || null);

  // Seed the dripper select with the most-recently-used dripper (impressions are newest-first),
  // falling back to the first active dripper. Only while adding (not editing) and still unset.
  const recentDripperId = useMemo(
    () => impressions.find((i) => i.dripper_id != null)?.dripper_id ?? null,
    [impressions]
  );
  useEffect(() => {
    if (editingId != null) return;
    setF((cur) => {
      if (cur.dripper !== "") return cur;
      const def = recentDripperId ?? activeDrippers[0]?.id ?? null;
      return def != null ? { ...cur, dripper: String(def) } : cur;
    });
  }, [editingId, recentDripperId, activeDrippers]);

  // Seed grinder / rpm / pour structure from the latest brew across *all* coffees (brewFeed is
  // oldest-first) — the grinder on the counter doesn't depend on the bag. A retired grinder is never
  // a default (an old bag's last brew may predate the current grinder). rpm comes from the last brew
  // on that grinder, else the grinder's fixed speed.
  useEffect(() => {
    if (editingId != null || !grinders) return;
    setF((cur) => {
      if (cur.grinder !== "") return cur;
      const active = grinders.filter((g) => g.active);
      const lastUsed = [...brewFeed].reverse().find((e) => e.grinder_id != null && active.some((g) => g.id === e.grinder_id));
      const g = active.find((x) => x.id === lastUsed?.grinder_id) ?? active[0];
      if (!g) return cur;
      const lastRpm = [...brewFeed].reverse().find((e) => e.grinder_id === g.id && e.rpm != null)?.rpm
        ?? (g.rpm_min != null && g.rpm_min === g.rpm_max ? g.rpm_min : null);
      const lastPour = brewFeed.length ? brewFeed[brewFeed.length - 1].pour_structure : null;
      return {
        ...cur,
        grinder: String(g.id),
        rpm: cur.rpm !== "" ? cur.rpm : lastRpm != null ? String(lastRpm) : "",
        pour: cur.pour !== "" ? cur.pour : lastPour ?? "",
      };
    });
  }, [editingId, grinders, brewFeed]);

  // Frozen default while adding: this bag's last logged state, else frozen if the bag has a freeze date.
  useEffect(() => {
    if (editingId != null) return;
    setF((cur) => {
      if (cur.frozen !== "") return cur;
      const last = impressions.find((i) => i.frozen != null)?.frozen;
      const frozen = last != null ? last : !!coffee.freeze_date;
      return { ...cur, frozen: frozen ? "1" : "0" };
    });
  }, [editingId, impressions, coffee.freeze_date]);

  // Starting-grind suggestion from the selected grinder's heuristic. Small bean is a per-bag judgement,
  // pre-ticked for Ethiopian coffees (small landrace beans throw more fines, doctrine §2.2/§2.5).
  const [smallBean, setSmallBean] = useState(() => /ethiopia/i.test(coffee.country || ""));
  const selectedGrinder = useMemo(() => (grinders || []).find((g) => String(g.id) === f.grinder), [grinders, f.grinder]);
  const grindSuggestion = useMemo(
    () => suggestGrind(selectedGrinder, { frozen: f.frozen === "1", natural: naturalFactor(coffee.process), smallBean }),
    [selectedGrinder, f.frozen, coffee.process, smallBean]
  );

  // Best scored brew of *this* bag, and the most recent "next time" plan, drive the hint card.
  const bestBrew = useMemo(() => {
    const scored = impressions.filter((i) => i.score != null);
    return scored.length ? scored.reduce((a, b) => ((b.score ?? 0) > (a.score ?? 0) ? b : a)) : null;
  }, [impressions]);
  const lastPlan = impressions.length ? impressions[0].next_time : null; // impressions are newest-first
  // Cold-start suggestion only when this bag has no brews yet.
  const suggestion = useMemo(
    () => (impressions.length === 0 ? suggestRecipe(brewFeed, coffee) : null),
    [impressions.length, brewFeed, coffee]
  );

  // Grind labels are grinder-specific, so a source brew's grind (and rpm) is only carried over when it
  // was ground on the grinder currently selected; otherwise the grind fields are left blank to dial in.
  const prefill = (src: { brew_type: string | null; dilution_g: number | null; dose_g: number | null; water_in_g: number | null; water_out_g: number | null; time_s: number | null; temp_c: number | null; grind: string | null; grind_microns: number | null; dripper_id?: number | null; grinder_id?: number | null; rpm?: number | null; pour_structure?: string | null }) => {
    setF((cur) => {
      const sameGrinder = src.grinder_id != null && String(src.grinder_id) === cur.grinder;
      return {
        ...cur,
        brew_type: src.brew_type === "hot" ? "hot" : "iced",
        dilution: src.dilution_g != null ? String(src.dilution_g) : cur.dilution,
        dose: src.dose_g != null ? String(src.dose_g) : "",
        water_in: src.water_in_g != null ? String(src.water_in_g) : "",
        water_out: src.water_out_g != null ? String(src.water_out_g) : "",
        time: fmtMinSec(src.time_s) ?? "",
        temp: src.temp_c != null ? String(src.temp_c) : "",
        grind: sameGrinder ? src.grind ?? "" : "",
        microns: sameGrinder && src.grind_microns != null ? String(src.grind_microns) : "",
        rpm: sameGrinder && src.rpm != null ? String(src.rpm) : cur.rpm,
        pour: src.pour_structure ?? cur.pour,
        dripper: src.dripper_id != null ? String(src.dripper_id) : cur.dripper,
      };
    });
  };

  // Load an entire entry into the form for editing (all fields, not just brew params).
  const startEdit = (i: Impression) => {
    setF({
      text: i.text ?? "",
      score: i.score != null ? String(i.score) : "",
      brew_type: i.brew_type === "hot" ? "hot" : "iced",
      dilution: i.dilution_g != null ? String(i.dilution_g) : "",
      dose: i.dose_g != null ? String(i.dose_g) : "",
      water_in: i.water_in_g != null ? String(i.water_in_g) : "",
      water_out: i.water_out_g != null ? String(i.water_out_g) : "",
      time: fmtMinSec(i.time_s) ?? "",
      temp: i.temp_c != null ? String(i.temp_c) : "",
      grind: i.grind ?? "",
      microns: i.grind_microns != null ? String(i.grind_microns) : "",
      tags: i.flavor_tags.join(", "),
      adjustment: i.adjustment ?? "",
      nextTime: i.next_time ?? "",
      dripper: i.dripper_id != null ? String(i.dripper_id) : "",
      grinder: i.grinder_id != null ? String(i.grinder_id) : "",
      rpm: i.rpm != null ? String(i.rpm) : "",
      pour: i.pour_structure ?? "",
      frozen: i.frozen == null ? "" : i.frozen ? "1" : "0",
    });
    setEditingId(i.id);
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setF(EMPTY); setError(null); };

  const setAsRecipe = async (src: { method: string | null; dose_g: number | null; water_in_g: number | null; time_s: number | null; temp_c: number | null }) => {
    if (!confirm("Replace this coffee's saved brew recipe with these settings?")) return;
    const recipe: BrewRecipe = {
      method: src.method ?? undefined,
      ratio: fmtRatio(src.dose_g, src.water_in_g) ?? undefined,
      temp_c: src.temp_c ?? undefined,
      time_s: src.time_s ?? undefined,
    };
    setBusy(true); setError(null);
    try {
      const updated = await api.update(coffee.id, { brew_recipe: recipe });
      onCoffeeChange(updated);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const onAdd = async () => {
    const input = toBrewInput(f);
    if (!input.text && input.score == null) { setError("add a note or a score"); return; }
    setBusy(true); setError(null);
    try {
      const { impression, coffee: updated } = await api.addImpression(coffee.id, input);
      setImpressions((cur) => [impression, ...cur]);
      onConsumed(updated);
      // keep the rig — usually the next brew uses the same dripper, grinder, rpm and pour structure
      setF({ ...EMPTY, dripper: f.dripper, grinder: f.grinder, rpm: f.rpm, pour: f.pour, frozen: f.frozen });
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  // Save edits to an existing entry (content only — portion count is untouched).
  const onSave = async () => {
    if (editingId == null) return;
    const input = toBrewInput(f);
    if (!input.text && input.score == null) { setError("add a note or a score"); return; }
    setBusy(true); setError(null);
    try {
      const updated = await api.updateImpression(coffee.id, editingId, input);
      setImpressions((cur) => cur.map((i) => (i.id === editingId ? updated : i)));
      cancelEdit();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const onDelete = async (id: number) => {
    if (!confirm("Delete this brew entry? (the portion stays consumed)")) return;
    setBusy(true); setError(null);
    try {
      await api.deleteImpression(coffee.id, id);
      setImpressions((cur) => cur.filter((i) => i.id !== id));
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <section>
      <div className="eyebrow mb-3">Brew log · {impressions.length}</div>

      {/* Practice-loop hint card: last plan, this bag's best brew, or a cold-start suggestion. */}
      {(lastPlan || bestBrew || suggestion) ? (
        <div className="card px-4 py-3 mb-4 max-w-2xl space-y-2 text-sm">
          {lastPlan ? (
            <div><span className="eyebrow mr-2">Planned last time</span><span className="text-paper-800">{lastPlan}</span></div>
          ) : null}
          {bestBrew ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="eyebrow">Best so far</span>
              {bestBrew.score != null ? <ScoreBadge score={bestBrew.score} /> : null}
              <span className="text-paper-600">{brewParamChips(bestBrew).join(" · ") || "no params"}</span>
              <button className="text-[10px] uppercase tracking-widest text-paper-500 hover:text-ink underline" onClick={() => prefill(bestBrew)}>Start from this</button>
              <button className="text-[10px] uppercase tracking-widest text-paper-500 hover:text-ink underline" onClick={() => setAsRecipe(bestBrew)} disabled={busy}>Set as recipe</button>
            </div>
          ) : null}
          {suggestion ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="eyebrow">Try starting from</span>
              <span className="text-paper-800">
                {suggestion.count} {suggestion.basis} brew{suggestion.count === 1 ? "" : "s"} · best {suggestion.best.score}:
              </span>
              <span className="text-paper-600">{brewParamChips(suggestion.best).join(" · ") || "no params"}</span>
              <button className="text-[10px] uppercase tracking-widest text-paper-500 hover:text-ink underline" onClick={() => prefill(suggestion.best)}>Start from this</button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Brew entry form */}
      <div className="space-y-3 mb-6 max-w-2xl">
        <div className="flex flex-wrap items-end gap-4">
          <label className="block w-28">
            <span className="label">Score /100</span>
            <input className="input tabular-nums" inputMode="decimal" placeholder="88.5"
              value={f.score} onChange={(e) => set("score", e.target.value)} />
          </label>
          <div>
            <span className="label">Type</span>
            <div className="flex gap-1">
              {(["iced", "hot"] as const).map((t) => (
                <button key={t} type="button"
                  onClick={() => setF((cur) => ({ ...cur, brew_type: t, dilution: t === "iced" && cur.dilution.trim() === "" ? "60" : cur.dilution }))}
                  className={`px-3 py-1 text-[10px] uppercase tracking-widest border ${f.brew_type === t ? "bg-ink text-paper-50 border-ink" : "bg-paper-50 text-paper-600 border-paper-300 hover:border-ink hover:text-ink"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-paper-600 pb-2">
            <input type="checkbox" checked={f.frozen === "1"} onChange={(e) => set("frozen", e.target.checked ? "1" : "0")} />
            From frozen
          </label>
          <label className="block w-28">
            <span className="label">Dilution g</span>
            <input className="input tabular-nums" inputMode="decimal" placeholder={f.brew_type === "iced" ? "60" : "0"}
              value={f.dilution} onChange={(e) => set("dilution", e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Dripper</span>
            {activeDrippers.length ? (
              <select className="input" value={f.dripper} onChange={(e) => set("dripper", e.target.value)}>
                <option value="">— none —</option>
                {activeDrippers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            ) : (
              <div className="text-xs text-paper-500 py-2">
                <Link to="/drippers" className="underline hover:text-ink">Add a dripper</Link>
              </div>
            )}
          </label>
          <label className="block">
            <span className="label">Grinder</span>
            {grinderOptions.length ? (
              <select className="input" value={f.grinder} onChange={(e) => set("grinder", e.target.value)}>
                <option value="">— none —</option>
                {grinderOptions.map((g) => <option key={g.id} value={g.id}>{g.name}{g.active ? "" : " (retired)"}</option>)}
              </select>
            ) : (
              <div className="text-xs text-paper-500 py-2">
                <Link to="/grinders" className="underline hover:text-ink">Add a grinder</Link>
              </div>
            )}
          </label>
        </div>

        <textarea
          className="input-box min-h-[70px]"
          placeholder="What did you think of this brew? — first sip, balance, surprises…"
          value={f.text}
          onChange={(e) => set("text", e.target.value)}
        />

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label={`Dose g${ratioPreview ? ` · ${ratioPreview}` : ""}`}><input className="input tabular-nums" inputMode="decimal" placeholder="15" value={f.dose} onChange={(e) => set("dose", e.target.value)} /></Field>
          <Field label="Water in g"><input className="input tabular-nums" inputMode="decimal" placeholder="180" value={f.water_in} onChange={(e) => set("water_in", e.target.value)} /></Field>
          <Field label="Water out g"><input className="input tabular-nums" inputMode="decimal" placeholder="145" value={f.water_out} onChange={(e) => set("water_out", e.target.value)} /></Field>
          <Field label="Time m.s"><input className="input tabular-nums" inputMode="decimal" placeholder="2.30" value={f.time} onChange={(e) => set("time", e.target.value)} /></Field>
          <Field label="Temp °C"><input className="input tabular-nums" inputMode="decimal" placeholder="92" value={f.temp} onChange={(e) => set("temp", e.target.value)} /></Field>
          <Field label="Grind"><input className="input" placeholder="med-fine / 2.5" value={f.grind} onChange={(e) => set("grind", e.target.value)} /></Field>
          <Field label="Grind µm"><input className="input tabular-nums" inputMode="numeric" placeholder="450" value={f.microns} onChange={(e) => set("microns", e.target.value)} /></Field>
          <Field label="RPM"><input className="input tabular-nums" inputMode="numeric" placeholder="800" value={f.rpm} onChange={(e) => set("rpm", e.target.value)} /></Field>
          <Field label="Pour structure"><input className="input" placeholder="2-6-5 or 50/50/50/40" value={f.pour} onChange={(e) => set("pour", e.target.value)} /></Field>
          <Field label="Flavor tags" wide><input className="input" placeholder="Jasmine, Mango" value={f.tags} onChange={(e) => set("tags", e.target.value)} /></Field>
          <Field label="Changed this time" wide><input className="input" placeholder="ground finer, 2 clicks" value={f.adjustment} onChange={(e) => set("adjustment", e.target.value)} /></Field>
          <Field label="Try next time" wide><input className="input" placeholder="drop temp to 90°C" value={f.nextTime} onChange={(e) => set("nextTime", e.target.value)} /></Field>
        </div>

        {grindSuggestion ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-paper-600">
            <span className="eyebrow">Suggested grind</span>
            <span className="text-paper-800 tabular-nums">{grindSuggestion.value}</span>
            <span>= {grindSuggestion.parts.join(" ")}</span>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={smallBean} onChange={(e) => setSmallBean(e.target.checked)} />
              small bean{coffee.bean_size != null ? ` (screen ${coffee.bean_size})` : ""}
            </label>
            <button type="button" className="text-[10px] uppercase tracking-widest text-paper-500 hover:text-ink underline"
              onClick={() => set("grind", String(grindSuggestion.value))}>Use</button>
            <span className="text-paper-400">starting point — let the drawdown correct it</span>
          </div>
        ) : null}

        <div className="flex gap-2 items-center">
          {editingId != null ? (
            <>
              <button className="btn-primary" onClick={onSave} disabled={busy}>
                {busy ? "Saving…" : "Save changes"}
              </button>
              <button type="button" className="btn-ghost" onClick={cancelEdit} disabled={busy}>Cancel</button>
              <span className="text-xs text-paper-500 self-center">editing — portion count unchanged</span>
            </>
          ) : (
            <>
              <button className="btn-primary" onClick={onAdd} disabled={busy}>
                {busy ? "Saving…" : portions > 0 ? "Log brew & use 1 portion" : "Log brew"}
              </button>
              <span className="text-xs text-paper-500 self-center">
                {portions > 0 ? `uses 1 of ${portions} left` : "0 left — logs without using a portion"}
              </span>
            </>
          )}
          {error ? <span className="text-red-700 text-sm self-center">{error}</span> : null}
        </div>
      </div>

      {/* Brew timeline (newest first) */}
      {impressions.length === 0 ? (
        <p className="text-sm text-paper-500">No brews logged yet.</p>
      ) : (
        <ul className="border-t border-paper-200 max-w-2xl">
          {impressions.map((i) => {
            const chips = brewParamChips(i);
            return (
              <li key={i.id} className="py-3 border-b border-paper-200 flex justify-between gap-4 items-start">
                <div className="flex-1 space-y-1.5">
                  <div className="flex items-center gap-2">
                    {i.score != null ? <ScoreBadge score={i.score} /> : null}
                    <span className="text-[10px] uppercase tracking-widest text-paper-500">
                      {new Date(i.logged_at + "Z").toLocaleString()}
                    </span>
                  </div>
                  {chips.length ? <div className="text-xs text-paper-600">{chips.join(" · ")}</div> : null}
                  {i.flavor_tags.length ? <div className="text-[11px] italic text-paper-600">{i.flavor_tags.join(" · ")}</div> : null}
                  {i.text ? <p className="whitespace-pre-wrap text-[15px] text-paper-800 leading-relaxed">{i.text}</p> : null}
                  {i.adjustment ? <div className="text-xs text-paper-600"><span className="eyebrow mr-2">Changed</span>{i.adjustment}</div> : null}
                  {i.next_time ? <div className="text-xs text-paper-600"><span className="eyebrow mr-2">Next</span>{i.next_time}</div> : null}
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <button
                    onClick={() => startEdit(i)}
                    className="text-[10px] uppercase tracking-widest text-paper-400 hover:text-ink"
                    disabled={busy}
                  >Edit</button>
                  <button
                    onClick={() => onDelete(i.id)}
                    className="text-[10px] uppercase tracking-widest text-paper-400 hover:text-red-700"
                    disabled={busy}
                  >Delete</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ScoreBadge({ score }: { score: number }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[11px] tabular-nums font-medium border border-ink bg-ink text-paper-50">
      {score}
    </span>
  );
}

function Field({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <label className={`block ${wide ? "col-span-2 sm:col-span-3" : ""}`}>
      <span className="label">{label}</span>
      {children}
    </label>
  );
}
